/**
 * Visibilità e Impact Analyzer (CF05–CF07) — CL03.
 *
 * Due insiemi di regole, entrambi puri:
 *
 * - **la visibilità è esclusiva.** Un campo è di processo, oppure di corsie,
 *   oppure di task: mai una combinazione. Cambiare modalità scarta la
 *   configurazione dell'altra, e il progettista deve saperlo *prima* di
 *   perderla.
 * - **il consenso si sblocca solo su tutto l'insieme.** Il pulsante di conferma
 *   resta spento finché ogni anomalia che lo richiede non è stata verificata, e
 *   un'anomalia **bloccante** lo tiene spento comunque: nessun consenso la
 *   salva, e offrire la casella sarebbe una bugia.
 *
 * L'analisi autorevole è del server. Qui si decide cosa mostrare e quando il
 * consenso è completo — mai cosa è permesso.
 */

import type { ElementAccess, FieldVisibility, ImpactAnomaly, ImpactResult } from "model/CustomFields";

export type VisibilityScope = "process" | "lanes" | "tasks";

/** Le tre modalità, nell'ordine in cui la dialog le offre. */
export const VISIBILITY_SCOPES: readonly VisibilityScope[] = ["process", "lanes", "tasks"];

/** La visibilità con cui un campo nasce: la meno sorprendente. */
export const defaultVisibility = (): FieldVisibility => ({
  scope: "process",
  access: "editable",
});

/** La modalità di una visibilità. */
export const scopeOf = (visibility: FieldVisibility): VisibilityScope => visibility.scope;

/** Le voci selezionate, vuote per lo scope di processo. */
export const entriesOf = (visibility: FieldVisibility): ElementAccess[] =>
  visibility.scope === "process" ? [] : visibility.entries;

/**
 * La visibilità seleziona questo elemento?
 *
 * È la domanda che una riga di runtime (una card) ha il diritto di fare: per lo
 * scope `tasks` l'elemento della riga (`task.designerid`) e le voci sono
 * designerid confrontabili direttamente, e un campo di un altro task non
 * compare. Per lo scope `lanes` la riga porta l'id numerico della corsia, non il
 * suo designerid: la decisione resta del server. Una visibilità di processo —
 * il default — seleziona tutto, e una definizione assente non nasconde niente
 * (percorso legacy). La voce di start (`bpmn:StartEvent`, P9) è una voce di
 * scope `tasks`: non seleziona nessuna riga, vale solo nel form di avvio.
 */
export function selectsElement(
  visibility: FieldVisibility | undefined,
  element: string | undefined
): boolean {
  if (!visibility || visibility.scope !== "tasks") return true;
  if (!element) return true;
  return visibility.entries.some((entry) => entry.elementId === element);
}

/**
 * Passa a un'altra modalità.
 *
 * `losesSelection` dice se il cambiamento butterebbe via una selezione già
 * fatta: la dialog deve chiedere conferma prima, non dopo. Passare a `process`
 * perde sempre la selezione; cambiare fra `lanes` e `tasks` anche, perché gli
 * id appartengono a insiemi diversi.
 *
 * Una modalità selettiva nasce con **tutte** le voci disponibili selezionate:
 * il default "tutti vedono tutto" si esprime così, perché fuori dalla
 * selezione = nascosto. `defaultAccess` è l'accesso con cui ogni voce nasce —
 * `readonly` per i tipi che il server assegna, che non sono mai scrivibili.
 */
export function changeScope(
  visibility: FieldVisibility,
  scope: VisibilityScope,
  available: readonly ElementChoice[] = [],
  defaultAccess: ElementAccess["access"] = "editable"
): { visibility: FieldVisibility; losesSelection: boolean } {
  if (visibility.scope === scope) return { visibility, losesSelection: false };

  const losesSelection = entriesOf(visibility).length > 0;
  return {
    visibility:
      scope === "process"
        ? { scope: "process", access: defaultAccess }
        : {
            scope,
            entries: available.map((choice) => ({
              elementId: choice.elementId,
              access: defaultAccess,
            })),
          },
    losesSelection,
  };
}

/**
 * Imposta l'accesso di una voce selezionata.
 *
 * L'accesso è per riferimento: la stessa corsia può essere in sola lettura
 * mentre un'altra è editabile.
 */
export function setEntryAccess(
  visibility: FieldVisibility,
  elementId: string,
  access: ElementAccess["access"]
): FieldVisibility {
  if (visibility.scope === "process") return visibility;

  const existing = visibility.entries.find((entry) => entry.elementId === elementId);
  const entries = existing
    ? visibility.entries.map((entry) =>
        entry.elementId === elementId ? { ...entry, access } : entry
      )
    : [...visibility.entries, { elementId, access }];

  return { scope: visibility.scope, entries };
}

/** Toglie una voce dalla selezione. */
export function removeEntry(
  visibility: FieldVisibility,
  elementId: string
): FieldVisibility {
  if (visibility.scope === "process") return visibility;
  return {
    scope: visibility.scope,
    entries: visibility.entries.filter((entry) => entry.elementId !== elementId),
  };
}

/** Una voce selezionabile: la sua label è quella che il progettista vede. */
export interface ElementChoice {
  elementId: string;
  label: string;
  /**
   * Per una corsia è il pool (`bpmn:Participant`); per un task è la swimlane.
   *
   * `null` per chi sta fuori da un pool o da una swimlane.
   *
   * `label` è `null` quando il pool non ha un nome: i modelli veri ne hanno
   * (uno dei tre di prova ha il pool senza nome) e l'intestazione mostrerebbe
   * `Participant_1q3u0w6`. Meglio nessuna intestazione che un id.
   */
  group?: { id: string; label: string | null } | null;
}

/** Le voci di un pool o una swimlane, come la dialog le mostra. */
export interface ChoiceGroup {
  /** `null` per le voci che non stanno in un pool. */
  id: string | null;
  label: string | null;
  choices: ElementChoice[];
}

/**
 * Le voci raggruppate dal loro pool o swimlane, nell'ordine in cui arrivano.
 *
 * È presentazione, non una regola: chi non ha un pool finisce in un gruppo
 * senza etichetta, che è come dire "qui non c'è niente da separare".
 */
export function groupChoices(choices: readonly ElementChoice[]): ChoiceGroup[] {
  const groups: ChoiceGroup[] = [];
  for (const choice of choices) {
    const id = choice.group?.id ?? null;
    const found = groups.find((group) => group.id === id);
    if (found) {
      found.choices.push(choice);
    } else {
      groups.push({ id, label: choice.group?.label ?? null, choices: [choice] });
    }
  }
  return groups;
}

/** Cerca task per nome, id della swimlane o nome dei partecipanti. */
export function searchChoiceGroups(
  choices: readonly ElementChoice[],
  query: string
): ChoiceGroup[] {
  const search = query.trim().toLocaleLowerCase();
  const groups = groupChoices(choices);
  if (!search) return groups;
  return groups.flatMap((group) => {
    if (`${group.id ?? ""} ${group.label ?? ""}`.toLocaleLowerCase().includes(search)) {
      return [group];
    }
    const matching = group.choices.filter((choice) =>
      choice.label.toLocaleLowerCase().includes(search)
    );
    return matching.length ? [{ ...group, choices: matching }] : [];
  });
}

/**
 * Gli id selezionati che il modello non ha più.
 *
 * Un riferimento a un elemento rimosso non è un dettaglio da nascondere: quel
 * campo non sarà compilabile da nessuno, e il progettista deve vederlo.
 */
export function missingReferences(
  visibility: FieldVisibility,
  knownElementIds: readonly string[]
): string[] {
  if (visibility.scope === "process") return [];
  const known = new Set(knownElementIds);
  return visibility.entries
    .map((entry) => entry.elementId)
    .filter((elementId) => !known.has(elementId));
}

/**
 * Il riepilogo da mostrare nella master.
 *
 * Strutturato, non una frase: la composizione del testo spetta al catalogo di
 * traduzioni, che ha i segnaposto.
 */
export function summariseVisibility(
  visibility: FieldVisibility
): { key: string; count: number; access: string } {
  if (visibility.scope === "process") {
    return {
      key: "custom_fields.visibility.summary_process",
      count: 0,
      access: visibility.access,
    };
  }
  return {
    key: "custom_fields.visibility.summary_selection",
    count: visibility.entries.length,
    access: "mixed",
  };
}

// ---------------------------------------------------------------------------
// Impact Analyzer
// ---------------------------------------------------------------------------

/** Le anomalie che nessun consenso può salvare. */
export const blockingAnomalies = (impact: ImpactResult): ImpactAnomaly[] =>
  impact.anomalies.filter((anomaly) => anomaly.blocking);

/** Le anomalie che richiedono una verifica esplicita. */
export const confirmableAnomalies = (impact: ImpactResult): ImpactAnomaly[] =>
  impact.anomalies.filter(
    (anomaly) => anomaly.requiresConfirmation && !anomaly.blocking
  );

/** Le anomalie puramente informative: si mostrano, non si spuntano. */
export const informationalAnomalies = (impact: ImpactResult): ImpactAnomaly[] =>
  impact.anomalies.filter(
    (anomaly) => !anomaly.requiresConfirmation && !anomaly.blocking
  );

/** Restano anomalie da verificare? */
export const pendingAcknowledgements = (
  impact: ImpactResult,
  acknowledged: ReadonlySet<string>
): ImpactAnomaly[] =>
  confirmableAnomalies(impact).filter((anomaly) => !acknowledged.has(anomaly.id));

/**
 * Il consenso è completo?
 *
 * Falso se resta un'anomalia da verificare, e **sempre** falso se ce n'è una
 * bloccante: offrire la conferma su una configurazione che lascerebbe un
 * processo non chiudibile sarebbe una bugia con una casella accanto.
 */
export function canConfirm(
  impact: ImpactResult | null,
  acknowledged: ReadonlySet<string>
): boolean {
  if (impact === null) return false;
  if (blockingAnomalies(impact).length > 0) return false;
  return pendingAcknowledgements(impact, acknowledged).length === 0;
}

/**
 * L'analisi è scaduta?
 *
 * Un'analisi vecchia descrive un modello che potrebbe non esistere più: il
 * consenso va chiesto su quella corrente, non stiracchiato su una precedente.
 */
export function isStale(expiresAt: string, now: Date): boolean {
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return true;
  return expiry.getTime() <= now.getTime();
}

/**
 * Il token da inviare, o niente.
 *
 * `confirmationToken` esiste solo quando un consenso è stato dato: inviarlo
 * senza aver verificato nulla lo renderebbe una formalità.
 */
export function tokensForSave(
  impact: ImpactResult | null,
  acknowledged: ReadonlySet<string>
): { impactToken?: string; confirmationToken?: string } {
  if (impact === null) return {};
  if (!canConfirm(impact, acknowledged)) return { impactToken: impact.impactToken };
  return {
    impactToken: impact.impactToken,
    confirmationToken: impact.confirmationToken ?? undefined,
  };
}
